/**
 * The `record_listings` collection layer: fixed-field declarations, the
 * listing-context enricher (`enrichListingField`), `computeListingChanges`, and the
 * adapters that surface a listed record's columns in a list grid. A list's items
 * are entries (see entries.ts): listing columns read the entry's `listing`, null
 * on a row that's only matched; record fields read its `record`. Listing columns
 * write top-level; schema-field changes partition into `context` jsonb.
 */

import {
  type AugerField,
  type EnrichableField,
  getFieldType,
  hasFieldType,
  isJunctionField,
  isReferenceDef,
  isReferenceField,
  isRelationField,
  isValueField,
} from "../fields/fields.js"
import * as group from "../fields/group.js"
import { getListParentFields, getRelationshipFields } from "../fields/relationships.js"
import {
  type EnrichOptions,
  resolveDirect,
  resolveDirectIds,
  resolveJunction,
  resolveJunctionTargetIds,
  resolveReference,
} from "../fields/resolve.js"
import { getSchema } from "../registries/schemas.js"
import { getType } from "../registries/types.js"
import type { Entry } from "./entries.js"
import {
  AGGREGATE_RELATIONS,
  type EnrichInput,
  partitionChanges,
  recordFixedFields,
  resolveLookup,
} from "./records.js"

// FIXED FIELDS ================================================================

const FIELDS: ReadonlyArray<EnrichableField> = [
  // `_record`: internal display key for the entry's record.
  {
    key: "_record",
    sourceKey: "record",
    type: "record",
    label: "Record",
    isEditable: false,
    // The record is the value; its id is the rawValue (reference contract).
    value: (item: unknown) => (item as Entry | null)?.record ?? null,
    rawValue: (item: unknown) => (item as Entry | null)?.record?.id ?? null,
  },
  {
    key: "is_primary",
    type: "boolean",
    label: "Primary",
    isEditable: true,
    width: 60,
  },
  {
    key: "label",
    type: "string",
    label: "Label",
    isEditable: true,
    ...group.byValue,
    width: 100,
  },
  { key: "position", type: "number", label: "Position", isHidden: true },
  { key: "context", type: "object", label: "Context", isHidden: true, isFilterable: false },
]

/** Top-level editable columns on `record_listings`; routes changes top-level vs
 *  `context` jsonb in `computeListingChanges`. */
export const LISTING_FIXED_KEYS: readonly string[] = ["is_primary", "label", "position", "context"]

/** FK columns mutated via dedicated methods (addItem, removeItem). */
export const LISTING_FOREIGN_KEYS: readonly string[] = ["list_id", "record_id"]

/** Whether a list item is listed: only then has it a listing to read and edit. */
export const hasListing = (item: unknown): boolean => (item as Entry | null)?.listing != null

export const listingFixedFields: AugerField[] = FIELDS.map((f) => {
  // Default accessor: the column on the item's listing, null on a row that's
  // only matched. A field can override `value`/`rawValue` above (`_record`).
  const dataKey = (f.sourceKey ?? f.key) as string
  const readColumn = (item: unknown) =>
    ((item as Entry | null)?.listing as Record<string, unknown> | null | undefined)?.[dataKey] ??
    null
  return {
    ...getFieldType(f.type),
    ...f,
    value: f.value ?? readColumn,
    rawValue: f.rawValue ?? readColumn,
    ...(f.isEditable ? { isEditableFor: hasListing } : {}),
  }
})

// MUTATION HELPER =============================================================

const LISTING_TOP_LEVEL = new Set(LISTING_FIXED_KEYS)
// Listing-system fields that shouldn't appear in changes payloads. `context`
// is deliberately NOT here — it's a real column (LISTING_FIXED_KEYS), and
// partitionChanges checks skipKeys before topLevelKeys, so having it in both
// sets made every `context` key silently dropped instead of replacing the
// blob wholesale — a caller-visible no-op with no error and no effect.
const LISTING_SKIP = new Set(["id", "list_id", "record_id", "record", "created_at", "updated_at"])

/**
 * Validate bare context keys against the list's LISTING schema — the schema at
 * `list.data.listing_schema_id`, which is the one the read path resolves
 * `listing.context` keys through. (The list record's own `schema_id` describes
 * fields whose values live in `list.data`, never in context.) Shared by the
 * update door (computeListingChanges) and the insert door (listings.add/addMany).
 *
 * A list that declares NO listing schema is deliberately let through
 * unchecked, not refused — see the comment on the `!schema` branch below for
 * why that is a fix, not a decision to leave listing context unvalidated.
 */
export function assertDeclaredContextKeys(
  bareKeys: string[],
  listingSchemaId: number | null | undefined,
  source: string
): void {
  if (!bareKeys.length) return
  const schema = listingSchemaId != null ? getSchema(listingSchemaId) : null
  if (listingSchemaId != null && !schema) {
    // Same distinction enrichRecord draws for a record's schema_id: the list
    // DOES declare a listing schema; the registry just can't resolve it —
    // either the host never populated it, or the id dangles. This is a real
    // declared contract that failed to resolve, so it still throws.
    throw new Error(
      `${source} names listing schema #${listingSchemaId}, which the registry ` +
        `cannot resolve — call registerSchemas() (auger.load() does) before ` +
        `writing, or fix the dangling data.listing_schema_id. ` +
        `Refusing ${bareKeys.map((k) => `"${k}"`).join(", ")}.`
    )
  }
  if (!schema) {
    // No listing schema declared: there is nothing to validate bare keys
    // against, so let them through instead of refusing every write. This was
    // previously an outright refusal, and that was a functional regression —
    // production carries ~13k listings across ~86 schema-less lists whose
    // context holds real, actively-read Luma/Airtable sync data
    // (checked_in_at, approval_status, luma_guest_id) that predates the
    // listing-schema convention entirely. Refusing those writes protected
    // nothing, since there was no schema to protect.
    //
    // This is NOT a decision that listing context should go unvalidated
    // forever, and it does not mirror records.ts's stance for the analogous
    // case: enrichRecord/planRecordWrite treats a record with no resolvable
    // schema the same way as one with a real schema — every key not a fixed
    // column is refused outright, because there's no known legacy corpus of
    // real data written to a schema-less record's `data`. Listings differ
    // only because of that grandfathered production data; once those lists
    // are migrated onto real listing schemas, this exception should be
    // removed and lists locked down the same way records already are.
    return
  }
  const declared = new Set((schema.fields ?? []).map((f) => f.key as string))
  const unknown = bareKeys.filter((key) => !declared.has(key))
  if (unknown.length) {
    throw new Error(
      `${source}: ${unknown.map((k) => `"${k}"`).join(", ")} ` +
        `${unknown.length === 1 ? "is" : "are"} not declared by the list's listing ` +
        `schema ("${schema.name}") — the listing grid resolves context through ` +
        `that schema, so an undeclared key is invisible there, and this service ` +
        `writes nothing it can't read back. A field on the LISTED record goes ` +
        `through records.update(listing.record_id, …).`
    )
  }
}

/**
 * Compute the Hasura `_set` mutation payload for listing edits.
 *
 * PRECONDITION for bare (non-column) keys: `listing` must carry the joined
 * `list { data }` (the service pre-reads select it) — the guard resolves the
 * list's LISTING schema from `list.data.listing_schema_id` to validate them.
 * A row read without it reads as schema-less (`listingSchemaId` is
 * `undefined`), so its bare keys are allowed unconditionally rather than
 * validated — the same outcome as a genuinely schema-less list, silently
 * rather than by refusing. Both service pre-reads
 * (fetchRecordListingById/fetchRecordListingsByIds) select `list { data }`,
 * so this only matters for a caller that invokes this function directly.
 *
 * The frontend's fetchListMembers is exactly such a caller, deliberately:
 * its projection omits `list { data }`, so the guard is inert on the listing
 * grid. That is a decision backed by production data — roughly 15-17 of the
 * 26 lists that declare a listing schema hold context keys the schema does
 * not declare — and the reasoning is written out where the projection is, in
 * auger-web/src/common/hasura/listings.ts. Read it before "fixing" this.
 */
export function computeListingChanges(
  listing: ListingTarget,
  changes: Record<string, unknown>
): { _set: Record<string, unknown>; hasChanges: boolean } {
  // `record.<key>` namespaces a grid column as belonging to the LISTED
  // record, not the listing (`listingRecordFixedFields` / `enrichListingField`
  // below rekey record fields exactly this way for display). Nothing routes
  // a `record.`-prefixed key to the record's own write path from here — it
  // would fall through to the jsonb branch below and land in
  // `context["record.<key>"]`, which no reader consults. Refuse it rather
  // than accept a write that changes nothing the caller can see changed.
  for (const key of Object.keys(changes)) {
    if (key.startsWith("record.")) {
      throw new Error(
        `computeListingChanges: "${key}" names a field on the LISTED record, not the ` +
          `listing — write it through records.update(listing.record_id, …) instead. ` +
          `Writing it here would land in context["${key}"], which nothing reads.`
      )
    }
  }

  // The bare spelling of the same mistake: a key that is neither a listing
  // column nor declared by the list's LISTING schema merges into `context`,
  // where no reader looks — the read path resolves context keys through the
  // listing schema's fields only. Same write-strict rule as planRecordWrite's
  // unknown-key refusal: refuse rather than report success on a dead write.
  const bareKeys = Object.keys(changes).filter(
    (key) => !LISTING_TOP_LEVEL.has(key) && !LISTING_SKIP.has(key)
  )
  // A LITERAL `context` replacement enforces the same declared-key rule —
  // otherwise add-then-update with a wholesale blob dodges the guard in two
  // calls. `context: null` (clear) has no keys and stays legal.
  const literal = changes.context
  const literalKeys =
    literal && typeof literal === "object" && !Array.isArray(literal) ? Object.keys(literal) : []
  const listingSchemaId = listing.list?.data?.listing_schema_id
  const id = listing.id
  assertDeclaredContextKeys(
    [...new Set([...bareKeys, ...literalKeys])],
    listingSchemaId,
    `listing #${id ?? "?"}`
  )

  return partitionChanges(listing as Record<string, unknown>, changes, {
    topLevelKeys: LISTING_TOP_LEVEL,
    jsonbColumn: "context",
    skipKeys: LISTING_SKIP,
  })
}

// LISTING-CONTEXT ADAPTERS ====================================================

/** Record fixed-fields surfaced in a listing context, rekeyed `record.X` and
 *  rewired to read from `listing.record.X`. */
export const listingRecordFixedFields: AugerField[] = recordFixedFields
  .filter((f) => ["name", "slug", "description", "updated_at"].includes(f.key as string))
  .map((f) => {
    // Read off the listing's wrapped record, so rawValue uses the same reader as
    // value, not the inherited record-shaped one.
    const read = (item: unknown) =>
      (item as { record?: Record<string, unknown> } | null)?.record?.[f.key as string] ?? null
    return {
      ...f,
      key: `record.${f.key}`,
      sourceKey: f.key as string,
      label: f.key === "name" ? "Name" : f.label,
      isEditable: f.key === "description",
      isHidden: f.key !== "description",
      value: read,
      rawValue: read,
    }
  })

// Editable in place: any value-kind field (scalars + their subtypes — currency,
// task_status, …), a junction, or a value relation (contacts and times: the row
// IS the value). Value fields mutate the listed record's data; junctions and
// value relations reconcile their rows. Other references and computed fields
// are read-only here.
//
// A fixed allowlist of six type NAMES lived here once and silently excluded
// every value subtype. Ask the type system what kind a field is; do not
// enumerate names.
const isValueRelationField = (type: string): boolean => {
  const def = getFieldType(type)
  return isReferenceDef(def) && (def.relationship === "contacts" || def.relationship === "times")
}
const isListingEditable = (type: string): boolean =>
  hasFieldType(type) &&
  (isValueField({ type }) || isJunctionField({ type }) || isValueRelationField(type))

/**
 * What `computeListingChanges` diffs against: the `record_listings` row's own
 * columns, optionally joined to its list.
 *
 * Spelled as the ROW rather than as either listing shape, because both satisfy
 * it and both call the door — the services layer's flat `ListingItem`
 * (columns only) and an entry's `listing`. Naming it is
 * what replaced a `Record<string, unknown>` parameter that accepted anything
 * at all, including a record; structural typing still lets an unrelated object
 * with compatible optional properties through, so this documents and narrows
 * the contract rather than sealing it.
 *
 * `list` is the guard join: `assertDeclaredContextKeys` resolves the list's
 * LISTING schema from `list.data.listing_schema_id`, so a listing read without
 * it validates nothing (see the note on that function).
 */
export type ListingTarget = {
  id?: number | string
  is_primary?: boolean | null
  label?: string | null
  position?: number | null
  context?: Record<string, unknown> | null
  list?: { data?: { listing_schema_id?: number | null } | null } | null
}

/**
 * Listing-context enricher — the parallel to records.ts's `enrichField` for the
 * `record_listings` grid, named distinctly because the two are NOT
 * interchangeable: this one's accessors unwrap `listing.record` and rekey
 * public keys `record.<key>` (grid + mutation routing tell record columns from
 * listing ones); records.ts's enrichField reads `item.data[key]` and would
 * silently resolve null forever against a listing row.
 */
export function enrichListingField(field: EnrichInput, options: EnrichOptions = {}): AugerField {
  const def = getFieldType(field.type)
  const sourceKey = field.key as string
  // `value`/`rawValue` assigned below before return, so widen the spread here.
  // Cast kept: the Field is built in steps, and the type system cannot see that
  // every branch below sets `value` and `rawValue` before it returns.
  const merged = {
    ...def,
    ...field,
    ...options,
    // `key` is the namespaced column identity; `sourceKey` is the bare key
    // consumers read/write record.data and create junction rows with.
    key: `record.${sourceKey}`,
    sourceKey,
  } as unknown as AugerField

  // schema_key: the field's key within its schema; null for aggregate relations
  // (they span unqualified rows), unset for fixed columns.
  const isAggregate = AGGREGATE_RELATIONS.has(sourceKey) || merged.type === "socials"
  if (merged.schema_id != null || isRelationField(merged)) {
    merged.schema_key = isAggregate ? null : sourceKey
  }

  // A listing wraps the record under `record`; accessors read off that. The
  // resolvers receive the already-unwrapped record, so `readIdFromRecord` reads
  // `data[sourceKey]` directly (no re-unwrap).
  const record = (item: unknown) => (item as Entry | null)?.record ?? null
  const readId = (item: unknown) => record(item)?.data?.[sourceKey] ?? null
  const readIdFromRecord = (rec: unknown) =>
    (rec as { data?: Record<string, unknown> } | null)?.data?.[sourceKey] ?? null

  merged.rawValue = readId

  // Related: a direct field whose relation the key names, off the listed record.
  if (merged.type === "related" && isReferenceDef(def)) {
    const relation = { ...def, relationship: sourceKey }
    const resolveVal = resolveDirect(relation)
    const resolveIds = resolveDirectIds(relation)
    if (resolveVal) merged.value = (item) => resolveVal(record(item))
    if (resolveIds) merged.rawValue = (item) => resolveIds(record(item))
    return merged
  }

  // Lookup: resolved off the listed record's links (see `resolveLookup`).
  if (merged.type === "lookup") {
    const lookup = resolveLookup(merged)
    merged.value = lookup ? (item) => lookup.value(record(item)) : () => null
    merged.rawValue = merged.value
    if (lookup) merged.format = lookup.format
    return merged
  }

  // Junction (link/tagging/attachment): both accessors derive from its
  // schema_key-scoped rows on the unwrapped record — `value` hops to entities,
  // `rawValue` is their ids.
  if (isReferenceDef(def) && isJunctionField(merged)) {
    const resolveVal = resolveJunction(def, sourceKey)
    const resolveIds = resolveJunctionTargetIds(def, sourceKey)
    merged.value = resolveVal ? (item) => resolveVal(record(item)) : readId
    merged.rawValue = resolveIds ? (item) => resolveIds(record(item)) : readId
    return merged
  }

  // A reference field. Schema-qualified item relations (contact/time/source)
  // resolve off their `schema_key`-scoped rows, shaped to arity; `rawValue` is
  // those rows' ids. Store-backed refs (user/schema/type) resolve off the raw id.
  if (isReferenceDef(def) && isReferenceField(merged)) {
    const resolve = resolveReference(
      def,
      readIdFromRecord,
      def.relationship ? sourceKey : undefined
    )
    if (resolve && def.relationship) {
      const rows = (item: unknown) => (resolve(record(item)) as { id: unknown }[]) ?? []
      merged.value = (item) => (merged.isArray ? rows(item) : (rows(item)[0] ?? null))
      merged.rawValue = (item) => {
        const ids = rows(item).map((row) => row.id)
        return merged.isArray ? ids : (ids[0] ?? null)
      }
    } else {
      merged.value = resolve ? (item) => resolve(record(item)) : readId
    }
    return merged
  }

  merged.value = readId
  return merged
}

/** Type-schema fields enriched for the listing grid, from the types store. */
export function getListingTypeSchemaFields(recordType: string): AugerField[] {
  if (!recordType) return []
  const type = getType(recordType)
  const schema = type?.schema
  if (!schema?.fields || schema.id == null) return []
  return schema.fields.map((f) =>
    enrichListingField(f, { schema_id: schema.id, isEditable: isListingEditable(f.type) })
  )
}

/**
 * The slice of a list item the record-field builders read: its record.
 *
 * `record` is REQUIRED, and that is the enforcement — the services layer's flat
 * `ListingItem` has no `record` at all, so handing these builders service rows
 * is a compile error instead of an empty field list. Spelled as this slice so a
 * host's own row with the same `record` (a pipeline's stage) fits too.
 *
 * "Required" is a compile-time promise, not a runtime one — it rides on
 * `record_listings.record_id` being `NOT NULL ... ON DELETE CASCADE`
 * (postgres/SCHEMA.md), so an orphaned listing can't exist in the DB. If that
 * FK is ever relaxed, a caller satisfying this type by construction (a cast,
 * an optimistic stub) can still hand `computeRecordSchemaSuperset` a record-less
 * row at runtime.
 */
export type ListedRecordRow = { record: { schema_id?: number | null } }

/** Union of distinct schema fields across the listed records, resolving each
 *  item's schema (by `record.schema_id`) via the schemas store. */
export function computeRecordSchemaSuperset(items: readonly ListedRecordRow[]): AugerField[] {
  // The distinct record schemas in play, one entry per schema id. `?.` guards
  // the runtime gap the type can't close — see the ListedRecordRow doc comment.
  const schemaIds = new Set(items.map((item) => item.record?.schema_id).filter((id) => id != null))

  // Their fields, deduped by key (first schema to declare a key wins).
  const fieldMap = new Map<string, AugerField>()
  for (const schemaId of schemaIds) {
    const schema = getSchema(schemaId)
    if (!schema?.fields || schema.id == null) continue
    for (const field of schema.fields) {
      if (fieldMap.has(field.key)) continue
      fieldMap.set(
        field.key,
        enrichListingField(field, {
          schema_id: schema.id,
          isEditable: isListingEditable(field.type),
        })
      )
    }
  }

  return Array.from(fieldMap.values())
}

/** A record-context field adapted to a listing: rekeyed `record.<key>`, and
 *  both accessors read the listed record, which a listing nests under `.record`. */
function toListingRecordField(field: AugerField): AugerField {
  const originalValue = field.value
  const originalRawValue = field.rawValue
  const record = (item: unknown) => (item as { record?: unknown } | null)?.record ?? null

  return {
    ...field,
    key: `record.${field.key}`,
    sourceKey: field.key as string,
    value: (item: unknown) => originalValue(record(item)),
    rawValue: (item: unknown) => originalRawValue(record(item)),
  }
}

/** Relationship fields adapted for listing context. */
export function buildListingRelationshipFields(): AugerField[] {
  return getRelationshipFields().map(toListingRecordField)
}

/** The list-parent fields (events/organizations/people) for listing context.
 *  Always offered: a list selects them only when its view uses one, so whether
 *  rows carry them can't decide whether the field exists. */
export function buildListingParentFields(): AugerField[] {
  return getListParentFields().map(toListingRecordField)
}

/** Complete listing-context record fields: type-schema fields + per-record schema
 *  superset + listing-record fixed fields + relationship fields. */
export function buildListingRecordFields(
  recordType: string,
  items: readonly ListedRecordRow[] = []
): AugerField[] {
  const typeFields = getListingTypeSchemaFields(recordType)
  const typeFieldKeys = new Set(typeFields.map((f) => f.key))

  const recordSchemaFields = computeRecordSchemaSuperset(items).filter(
    (f) => !typeFieldKeys.has(f.key)
  )

  const listingRelationshipFields = [
    ...buildListingRelationshipFields(),
    ...buildListingParentFields(),
  ]

  // Tag by source identity (see Field.group) so listing views section the same
  // way record detail views do.
  return [
    ...typeFields.map((f) => ({ ...f, group: "typeSchema" as const })),
    ...recordSchemaFields.map((f) => ({ ...f, group: "recordSchema" as const })),
    ...listingRecordFixedFields.map((f) => ({ ...f, group: "recordColumn" as const })),
    ...listingRelationshipFields.map((f) => ({ ...f, group: "recordSchema" as const })),
  ]
}
